import { Environment } from '../interfaces/IBaseAPIClient';
import { ICentralDataManagerAPIClient } from './interfaces/ICentralDataManagerAPIClient';
import { IGameTemplateAPIClient, IQuestionTemplateAPIClient, IGameQuestionsAPIClient } from '../templates';
import { PublicPrivateType, SortDirection, GradeTarget, SortType } from "../BaseAPIClient";
import { IUserProfile } from '../../Models/IUserProfile';
import { IAuthAPIClient } from '../auth';
import { IUserAPIClient } from '../user';
import { UserParser } from '../../Parsers/UserParser';
import {
  getCurrentUser,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';

export const userProfileLocalStorage = 'righton_userprofile';

// JSON.stringify on an Error yields "{}" (its props are non-enumerable), which is
// how these paths ended up reporting 'upload failed: {}'. Keep the real message and
// the exception name, and add context in front of it.
const withContext = (context: string, error: any): Error => {
  const detail = error?.message ?? String(error ?? '');
  const err = new Error(detail.length > 0 ? `${context}: ${detail}` : context);
  err.name = error?.name ?? 'Error';
  return err;
};

export class CentralDataManagerAPIClient implements ICentralDataManagerAPIClient{
  protected env: Environment;
  protected authAPIClient: IAuthAPIClient;
  protected userAPIClient: IUserAPIClient;
  protected gameTemplateAPIClient: IGameTemplateAPIClient;
  protected gameQuestionsAPIClient: IGameQuestionsAPIClient;
  protected questionTemplateAPIClient: IQuestionTemplateAPIClient;

  constructor (
    env: Environment,
    authAPIClient: IAuthAPIClient,
    userAPIClient: IUserAPIClient,
    gameTemplateAPIClient: IGameTemplateAPIClient,
    gameQuestionsAPIClient: IGameQuestionsAPIClient,
    questionTemplateAPIClient: IQuestionTemplateAPIClient,
  ) {
    this.env = env;
    this.authAPIClient = authAPIClient;
    this.userAPIClient = userAPIClient;
    this.gameTemplateAPIClient = gameTemplateAPIClient;
    this.gameQuestionsAPIClient = gameQuestionsAPIClient;
    this.questionTemplateAPIClient = questionTemplateAPIClient;
  } 
  
  public initGames = async () => {
    let games = [];
    const response = await this.gameTemplateAPIClient.listGameTemplatesByDate(PublicPrivateType.PUBLIC, 12, null, SortDirection.DESC, null, [], null, true);
    if (response){
      games.push(...response.gameTemplates);
      while (games.length < 12) {
        if (!response.nextToken) break;
        const nextResponse = await this.gameTemplateAPIClient.listGameTemplatesByDate(PublicPrivateType.PUBLIC, 12, response.nextToken, SortDirection.DESC, null, [], null, true);
        if (nextResponse && nextResponse.gameTemplates) {
          games.push(...nextResponse.gameTemplates);
          response.nextToken = nextResponse.nextToken;
        } else {
          break;
        }
      }
      return { nextToken: response.nextToken, games };
    }
    return { nextToken: null, games: [] };
  };

  public initQuestions = async () => {
    let questions = [];
    const response = await this.questionTemplateAPIClient.listQuestionTemplatesByDate(PublicPrivateType.PUBLIC, 24, null, SortDirection.DESC, null, [], null);
    if (response) {
      questions.push(...response.questionTemplates);
      while (questions.length < 24) {
        if (!response.nextToken) break;
        const nextResponse = await this.questionTemplateAPIClient.listQuestionTemplatesByDate(PublicPrivateType.PUBLIC, 24, response.nextToken, SortDirection.DESC, null, [], null);
        if (nextResponse && nextResponse.questionTemplates) {
          questions.push(...nextResponse.questionTemplates);
          response.nextToken = nextResponse.nextToken;
        } else {
          break;
        }
      }
      return { nextToken: response.nextToken, questions };
    }
    return { nextToken: null, questions: [] };
  };

  public favoriteGameTemplate = async (gameId: string, user: IUserProfile) => {
    let newFavoriteGameTemplateIds = user.favoriteGameTemplateIds ? JSON.parse(JSON.stringify(user.favoriteGameTemplateIds)) : [];
    const isFav = newFavoriteGameTemplateIds.includes(gameId);
    if (isFav === true)
      newFavoriteGameTemplateIds = newFavoriteGameTemplateIds.filter((id: string) => id !== gameId);
    else 
      newFavoriteGameTemplateIds.push(gameId);
    return await this.userAPIClient.updateUser({ id: user.dynamoId ?? '', favoriteGameTemplateIds: JSON.stringify(newFavoriteGameTemplateIds) });
  };

  public favoriteQuestionTemplate = async (questionId: string, user: IUserProfile) => {
    let newFavoriteQuestionTemplateIds = user.favoriteQuestionTemplateIds ? JSON.parse(JSON.stringify(user.favoriteQuestionTemplateIds)) : [];
    const isFav = newFavoriteQuestionTemplateIds.includes(questionId);
    if (isFav === true)
      newFavoriteQuestionTemplateIds = newFavoriteQuestionTemplateIds.filter((id: string) => id !== questionId);
    else 
      newFavoriteQuestionTemplateIds.push(questionId);
    return await this.userAPIClient.updateUser({ id: user.dynamoId ?? '', favoriteQuestionTemplateIds: JSON.stringify(newFavoriteQuestionTemplateIds) });
  };

  public searchForGameTemplates = async (type: PublicPrivateType, limit: number | null, nextToken: string | null, search: string, sortDirection: SortDirection, sortType: SortType, gradeTargets: GradeTarget[], favIds: string[] | null, isLibrary?: boolean, userId?: string) => {
    switch(sortType){
      case SortType.listGameTemplatesByDate: {
        let response;
        if (!isLibrary) 
          response = await this.gameTemplateAPIClient.listGameTemplatesByDate(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.gameTemplateAPIClient.listGameTemplatesByUserDate(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, games: response.gameTemplates };
        }
        break;
      }
      case SortType.listGameTemplatesByGrade: {
        let response;
        if (!isLibrary)
         response = await this.gameTemplateAPIClient.listGameTemplatesByGrade(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.gameTemplateAPIClient.listGameTemplatesByUserGrade(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, games: response.gameTemplates };
        }
        break;
      }
      case SortType.listGameTemplatesByQuestionCount: {
        let response;
        if (!isLibrary)
          response = await this.gameTemplateAPIClient.listGameTemplatesByQuestionTemplatesCount(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.gameTemplateAPIClient.listGameTemplatesByUserPublicQuestionTemplatesCount(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, games: response.gameTemplates };
        }
        break;
      }
      case SortType.listGameTemplates:
      default: {
        let response;
        if (!isLibrary)
          response = await this.gameTemplateAPIClient.listGameTemplates(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.gameTemplateAPIClient.listGameTemplatesByUserDate(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, games: response.gameTemplates };
        }
        break; 
      }
    }
    return {nextToken: null, games: []};
  };

  public searchForQuestionTemplates = async (type: PublicPrivateType, limit: number | null, nextToken: string | null, search: string | null, sortDirection: SortDirection, sortType: SortType, gradeTargets: GradeTarget[], favIds: string[] | null, isLibrary?: boolean, userId?: string) => {
    switch(sortType){
      case SortType.listQuestionTemplatesByDate: {
        let response;
        if (!isLibrary)
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByDate(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByUserDate(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, questions: response.questionTemplates };
        }
        break;
      }
      case SortType.listQuestionTemplatesByGrade: {
        let response;
        if (!isLibrary)
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByGrade(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByUserGrade(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, questions: response.questionTemplates };
        }
        break;
      }
      case SortType.listQuestionTemplatesByGameCount: {
        let response;
        if (!isLibrary)
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByGameTemplatesCount(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByUserPublicGameTemplatesCount(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, questions: response.questionTemplates };
        }
        break;
      }
      case SortType.listQuestionTemplates:
      default: {
        let response;
        if (!isLibrary)
          response = await this.questionTemplateAPIClient.listQuestionTemplates(type, limit, nextToken, sortDirection, search, gradeTargets, favIds);
        else 
          response = await this.questionTemplateAPIClient.listQuestionTemplatesByUserDate(type, limit, nextToken, sortDirection, search, gradeTargets, favIds, userId ?? '');
        if (response){
          return { nextToken: response.nextToken, questions: response.questionTemplates };
        }
        break; 
      }
    }
    return {nextToken: null, questions: []};
  };

  public removeQuestionTemplateFromGameTemplate = async (type: PublicPrivateType, questionId: string, gameId: string) => {
    const gameQuestionIds = await this.questionTemplateAPIClient.getQuestionTemplateJoinTableIds(type, questionId);
    const gameQuestionId = gameQuestionIds.find((id) => id === gameId);

    if (gameQuestionId) {
      try {
        await this.gameQuestionsAPIClient.deleteGameQuestions(type, gameQuestionId);
        return true;
      } catch (error: any) {
        throw new Error(`Failed to delete game question associated with question template: ${error.message}`);
      }
    }
    return false;
  };

  public deleteQuestionTemplate = async (type: PublicPrivateType, questionId: string): Promise<boolean> => {
    try {
      // Join rows have to go first, but both paths end in the same template delete.
      // This whole chain MUST be awaited: callers (central_v2 MyLibrary/ExploreQuestions)
      // refetch the library on the line after `await deleteQuestionTemplate(...)`, so a
      // floating promise here means the refetch starts two round trips ahead of the delete
      // and reliably returns the question that was just deleted.
      const gameQuestionIds = await this.questionTemplateAPIClient.getQuestionTemplateJoinTableIds( type, questionId);
      if (gameQuestionIds && gameQuestionIds.length > 0) {
        await Promise.all(
          gameQuestionIds.map((gameQuestionId) =>
            this.gameQuestionsAPIClient.deleteGameQuestions(type, gameQuestionId),
          ),
        );
      }
      return await this.questionTemplateAPIClient.deleteQuestionTemplate(type, questionId);
    } catch (error: any) {
        throw new Error(`Failed to delete question template: ${error.message}`);
    }
  };

  public refreshLocalUserProfile = async () => {
    const localUser = window.localStorage.getItem(userProfileLocalStorage);
    const parsedLocalUser = localUser ? JSON.parse(localUser) : null;
    if (!parsedLocalUser) return;

    const result = this.userAPIClient.getUserByUserName(parsedLocalUser.userName).then((updatedUser) => {
      if (updatedUser !== null){
        const userProfile = { ...parsedLocalUser, ...updatedUser };
        this.setLocalUserProfile(userProfile);
        return userProfile;
      } else {
        this.clearLocalUserProfile();
        return null;
      }
    });
    return result;
  }

  public getLocalUserProfile = () => {
    const profile = window.localStorage.getItem(userProfileLocalStorage);
    if (profile){
      return JSON.parse(profile) as IUserProfile;
    }
    return null;
  };

  public setLocalUserProfile = (userProfile: IUserProfile) => {
    window.localStorage.setItem(userProfileLocalStorage, JSON.stringify(userProfile));
  }

  public clearLocalUserProfile = () => {
    window.localStorage.removeItem(userProfileLocalStorage);
  }

  public getUser = async (cognitoId: string) => {
    const userProfile = await this.userAPIClient.getUserByCognitoId(cognitoId);
    if (userProfile !== null){
      this.setLocalUserProfile(userProfile);
      return userProfile;
    }
    return null;
  }

  public loginUserAndRetrieveUserProfile = async (userName: string, password: string) => {
    let userProfile = null;
    try {
      await this.authAPIClient.awsSignIn(userName, password);
      const currentCognitoUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      if (!attributes || !attributes.nickname) 
        return null;
      const currentDynamoDBUser = await this.userAPIClient.getUserByUserName(attributes.nickname);
      if  (currentDynamoDBUser !== null){
        userProfile = {
          cognitoId: currentCognitoUser.userId,
          ...currentDynamoDBUser
        };
      this.setLocalUserProfile(userProfile); 
      this.authAPIClient.isUserAuth = true;
     }
      return userProfile;
    } catch (error: any) {
      // rethrow as-is: re-wrapping discards `name` (e.g. UserUnAuthenticatedException)
      throw error;
    }
  };

  public loginGoogleAndRetrieveUserProfile = async () => {
    let userProfile = null;
    try {
      await this.authAPIClient.awsSignInFederated();
      const currentSession = await this.authAPIClient.getCurrentSession();
      const cognitoId = currentSession?.userSub;
      if (!cognitoId) 
        return null;
      const currentDynamoDBUser = await this.userAPIClient.getUser(cognitoId);
      if  (currentDynamoDBUser !== null){
        userProfile = {
          cognitoId: cognitoId,
          ...currentDynamoDBUser
        };
      }
      if  (currentDynamoDBUser && userProfile){
        this.setLocalUserProfile(userProfile); 
        this.authAPIClient.isUserAuth = true;
      }
      return userProfile;
    } catch (error: any) {
      // rethrow as-is: re-wrapping discards `name` (e.g. UserUnAuthenticatedException)
      throw error;
    }
  };

  // Best-effort teardown of a half-created signup. Never masks the original
  // failure -- the caller always sees why signup actually failed.
  private cleanupPartialSignUp = async (user: IUserProfile) => {
    try {
      await this.authAPIClient.awsUserCleaner(user);
    } catch (cleanupError: any) {
      console.error('userCleaner failed after signup error:', cleanupError);
    }
  };

  public signUpSendConfirmationCode = async (user: IUserProfile) => {
    return this.authAPIClient.awsSignUp(user.userName, user.email, user.password ?? '');
  };

  public signUpConfirmAndBuildBackendUser = async (user: IUserProfile, confirmationCode: string, frontImage: File, backImage: File) => {
    let createUserInput = UserParser.parseAWSUserfromAuthUser(user);
    let updatedUser = JSON.parse(JSON.stringify(user));
    try {
      await this.authAPIClient.awsConfirmSignUp(user.email, confirmationCode);
    } catch (error: any) {
      // rethrow as-is: re-wrapping discards `name` (e.g. CodeMismatchException)
      throw error;
    }
    try {
      await this.authAPIClient.awsSignIn(user.email, user.password ?? '');
      const currentUser = await getCurrentUser();
      const dynamoId = uuidv4();
      // record identifiers BEFORE any fallible work, so a later failure still
      // leaves the cleaner something it can actually delete
      updatedUser = { ...updatedUser, id: dynamoId, dynamoId: dynamoId, cognitoId: currentUser.userId };
      const images = await Promise.all([
        this.authAPIClient.awsUploadImagePrivate(frontImage) as any,
        this.authAPIClient.awsUploadImagePrivate(backImage) as any
      ]);
      const frontIdPath = images[0]?.path;
      const backIdPath = images[1]?.path;
      // frontIdPath/backIdPath are nullable in the schema, so a resolved-but-pathless
      // upload would otherwise write a half-built account with no ID images
      if (!frontIdPath || !backIdPath) {
        throw new Error('Your Teacher ID images could not be uploaded. Please try again.');
      }
      updatedUser = { ...updatedUser, frontIdPath, backIdPath };
      createUserInput = { ...createUserInput, id: dynamoId, frontIdPath, backIdPath, cognitoId: currentUser.userId, dynamoId: dynamoId };

      const randomIndex = Math.floor(Math.random() * 5) + 1;
      
      updatedUser = { ...createUserInput, profilePicPath: `defaultProfilePic${randomIndex}.jpg`};
      // createdAt/updatedAt are stamped server-side and are not fields on
      // CreateUserInput, so they only exist on the mutation response - merge it
      // back in or the profile in state has no account-created date
      const createdUser = await this.userAPIClient.createUser(updatedUser);
      if (createdUser)
        updatedUser = { ...updatedUser, ...createdUser };
      this.setLocalUserProfile(updatedUser);
      this.authAPIClient.isUserAuth = true;

      return { updatedUser, images };
    } catch (error: any) {
      await this.cleanupPartialSignUp(updatedUser);
      throw error;
    }
  };


  public signUpGoogleBuildBackendUser = async (user: IUserProfile, frontImage: File, backImage: File) => {
    // Need to put it in Email into user.
    let getEmail = await this.authAPIClient.getUserEmail();
    if(getEmail){
      user.email = getEmail;
    }
    let createUserInput = UserParser.parseAWSUserfromAuthUser(user);
    let updatedUser = JSON.parse(JSON.stringify(user));
    let firstName = createUserInput.firstName;
    let lastName = createUserInput.lastName;
    try {
      const currentUser = await getCurrentUser();
      const dynamoId = uuidv4();
      updatedUser = { ...updatedUser, id: dynamoId, dynamoId: dynamoId, cognitoId: currentUser.userId };
      const images = await Promise.all([
        this.authAPIClient.awsUploadImagePrivate(frontImage) as any,
        this.authAPIClient.awsUploadImagePrivate(backImage) as any
      ]);
      const frontIdPath = images[0]?.path;
      const backIdPath = images[1]?.path;
      if (!frontIdPath || !backIdPath) {
        throw new Error('Your Teacher ID images could not be uploaded. Please try again.');
      }
      updatedUser = { ...updatedUser, frontIdPath, backIdPath };

      createUserInput = { ...createUserInput, id: dynamoId, firstName, lastName, frontIdPath, backIdPath, cognitoId: currentUser.userId, dynamoId: dynamoId };
      const randomIndex = Math.floor(Math.random() * 5) + 1;

      updatedUser = { ...createUserInput, profilePicPath: `defaultProfilePic${randomIndex}.jpg` };
      // see signUpConfirmAndBuildBackendUser: createdAt only comes back on the response
      const createdUser = await this.userAPIClient.createUser(updatedUser);
      if (createdUser)
        updatedUser = { ...updatedUser, ...createdUser };
      this.setLocalUserProfile(updatedUser);
      this.authAPIClient.isUserAuth = true;
      //TODO: set user status to LOGGED_IN
      return { updatedUser, images };

    } catch (error: any) {
      await this.cleanupPartialSignUp(updatedUser);
      throw error;
    }
  };

  public userProfileInformationUpdate = async (user: IUserProfile, oldUser: IUserProfile, frontImage?: File | null,
    backImage?: File | null
  ) => {
    let createUserInput = UserParser.parseAWSUserfromAuthUser(user);
    let updatedUser = JSON.parse(JSON.stringify(createUserInput));

    let oldUserInput = UserParser.parseAWSUserfromAuthUser(oldUser);

    if(updatedUser.userName != oldUserInput.userName){
      try {
        await this.authAPIClient.updateCognitoUsername(updatedUser.userName)
      }
      catch (error: any) {
        throw withContext('Could not update username', error);
      }
    }
    
    if(frontImage || backImage){
      if (frontImage) {
        try {
          const uploadedFront = await this.authAPIClient.awsUploadImagePrivate(frontImage) as any;
          updatedUser = { ...updatedUser, frontIdPath: uploadedFront.path };
        } catch (error: any) {
          throw withContext('Front image upload failed', error);
        }
      }
      if (backImage) {
        try {
          const uploadedBack = await this.authAPIClient.awsUploadImagePrivate(backImage) as any;
          updatedUser = { ...updatedUser, backIdPath: uploadedBack.path };
        } catch (error: any) {
          throw withContext('Back image upload failed', error);
        }
      }
    }

    // merge the response back in so server-side fields (createdAt/updatedAt) survive
    // the round trip through parseAWSUserfromAuthUser, which cannot carry them
    const savedUser = await this.userAPIClient.updateUser(updatedUser);
    if (savedUser)
      updatedUser = { ...updatedUser, ...savedUser };
    this.setLocalUserProfile(updatedUser);
    return { updatedUser };
  };


  public userProfileImageUpdate = async (user: IUserProfile, newProfilePic: File | null) => {
    let createUserInput = UserParser.parseAWSUserfromAuthUser(user);
    let updatedUser = JSON.parse(JSON.stringify(createUserInput));

    if (newProfilePic) {
      try {
        const uploadedImage = await this.authAPIClient.awsUploadImagePrivate(newProfilePic) as any;
        updatedUser = { ...updatedUser, profilePicPath: uploadedImage.path };
      } catch (error: any) {
        throw withContext('Profile image upload failed', error);
      }
    }
    // see userProfileInformationUpdate: keeps createdAt/updatedAt on the profile
    const savedUser = await this.userAPIClient.updateUser(updatedUser);
    if (savedUser)
      updatedUser = { ...updatedUser, ...savedUser };
    this.setLocalUserProfile(updatedUser);
    return { updatedUser };
  };


  public signOut = async () => {
    await this.authAPIClient.awsSignOut();
    this.authAPIClient.isUserAuth = false;
    this.clearLocalUserProfile();
  };
}